<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-shield-account</v-icon>
        <v-toolbar-title>Administradores de Grupo</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="openForm(null)" title="Nuevo administrador"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="admins" :loading="loading" item-key="id" :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }">
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
          <v-chip x-small v-if="item.id === currentUserId" color="primary" outlined>Tú</v-chip>
        </template>
        <template v-slot:item.created_by="{ item }">
          {{ item.created_by ? item.created_by.name : 'Pivot Fleet' }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey'" dark>
            {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn icon small color="primary" @click="openForm(item)" title="Editar"><v-icon>mdi-pencil</v-icon></v-btn>
          <v-btn icon small v-if="item.id !== currentUserId" :color="item.status === STATUS_ACTIVE ? 'error' : 'success'"
            @click="toggleStatus(item)" :title="item.status === STATUS_ACTIVE ? 'Desactivar' : 'Activar'">
            <v-icon>{{ item.status === STATUS_ACTIVE ? 'mdi-account-cancel' : 'mdi-account-check' }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="560px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-shield-account</v-icon>
          {{ editing ? 'Editar administrador' : 'Nuevo Administrador de Grupo' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.name" label="Nombre *" outlined dense :rules="[required]"></v-text-field></v-col>
              <v-col cols="6"><v-text-field v-model="form.lastname" label="Apellidos *" outlined dense :rules="[required]"></v-text-field></v-col>
            </v-row>
            <v-text-field v-model="form.email" label="Email *" outlined dense :rules="[required, email]"></v-text-field>
            <v-text-field v-model="form.phone" label="Teléfono" outlined dense></v-text-field>
            <v-alert v-if="!editing" type="info" text dense class="mb-0">
              Recibirá un email para establecer su contraseña.
            </v-alert>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ editing ? 'Guardar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import GrupoPanelService from '@/services/GrupoPanelService'
import { STATUS_ACTIVE, STATUS_INACTIVE, apiData, apiError } from '@/grupo'

const emptyForm = () => ({ name: '', lastname: '', email: '', phone: '' })

export default {
  name: 'GrupoAdmins',

  data: () => ({
    STATUS_ACTIVE,
    admins: [],
    loading: false,
    dialog: false,
    editing: null,
    valid: false,
    saving: false,
    form: emptyForm(),
    headers: [
      { text: 'Nombre', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Creado por', value: 'created_by', sortable: false },
      { text: 'Fecha de alta', value: 'created_at' },
      { text: 'Estado', value: 'status', align: 'center' },
      { text: 'Acciones', value: 'action', sortable: false, align: 'end' }
    ]
  }),

  computed: {
    currentUserId () {
      return this.$store.getters.CURRENT_USER.id
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    required: v => !!v || 'Obligatorio',
    email: v => /.+@.+\..+/.test(v || '') || 'Email no válido',

    formatDate (ts) {
      return ts ? moment.unix(ts).format('DD/MM/YYYY') : '—'
    },

    async load () {
      this.loading = true
      try {
        this.admins = apiData(await GrupoPanelService.admins()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los administradores'))
      } finally {
        this.loading = false
      }
    },

    openForm (admin) {
      this.editing = admin
      this.form = admin
        ? { name: admin.name, lastname: admin.lastname, email: admin.email, phone: admin.phone }
        : emptyForm()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },

    async save () {
      this.saving = true
      try {
        if (this.editing) {
          await GrupoPanelService.updateAdmin(this.editing.id, this.form)
          this.$toast.success('Administrador actualizado')
        } else {
          await GrupoPanelService.createAdmin(this.form)
          this.$toast.success('Administrador creado. Recibirá un email de acceso.')
        }
        this.dialog = false
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar el administrador'))
      } finally {
        this.saving = false
      }
    },

    async toggleStatus (admin) {
      const activate = admin.status !== STATUS_ACTIVE
      const ok = await this.$confirm(activate
        ? `¿Activar a ${admin.name} ${admin.lastname}?`
        : `¿Desactivar a ${admin.name} ${admin.lastname}? No podrá acceder, pero no se borra.`, { title: activate ? 'Activar' : 'Desactivar' })
      if (!ok) return
      try {
        await GrupoPanelService.setAdminStatus(admin.id, activate ? STATUS_ACTIVE : STATUS_INACTIVE)
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cambiar el estado'))
      }
    }
  }
}
</script>
